import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const TenantContext = createContext({
  tenant: null,
  slug: null,
  features: null,
  loading: true,
});

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

export function detectTenantSlug() {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');

  // 1. Subdomain — ONLY on production domain shangrilux.com
  if (
    parts.length === 3 &&
    parts[1] === 'shangrilux' &&
    parts[2] === 'com' &&
    !['www', 'api', 'superadmin', 'app'].includes(parts[0])
  ) {
    return parts[0];
  }

  // 2. Query param (priorità su localStorage)
  const params = new URLSearchParams(window.location.search);
  const fromParam = params.get('tenant');
  if (fromParam) return fromParam;

  // 3. localStorage — solo per route che richiedono il tenant context
  //    (evita chiamate superflue dalla landing/marketing page)
  const path = window.location.pathname;
  const TENANT_PATHS = ['/panel', '/booking', '/g/', '/key/', '/access/', '/fornitore'];
  if (TENANT_PATHS.some(p => path.startsWith(p))) {
    return localStorage.getItem('tenant_slug') || null;
  }

  return null;
}

// Axios interceptor: inject X-Tenant-Slug on every request
const tenantSlug = detectTenantSlug();
if (tenantSlug) {
  // Persist for api.js interceptor (survives hash navigation)
  localStorage.setItem('tenant_slug', tenantSlug);
  axios.interceptors.request.use(config => {
    config.headers['X-Tenant-Slug'] = tenantSlug;
    return config;
  });
}

export function TenantProvider({ children }) {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const slug = tenantSlug;

  const fetchConfig = useCallback(async () => {
    if (!slug) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${BACKEND}/api/tenant/config`);
      setTenant(data);
    } catch {
      setTenant(null);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  return (
    <TenantContext.Provider value={{ tenant, slug, features: tenant?.features, whitelabel: tenant?.whitelabel, loading, refreshTenant: fetchConfig }}>
      {children}
    </TenantContext.Provider>
  );
}

export const useTenant = () => useContext(TenantContext);
